import { useState } from "react";
import { SelectChangeEvent, useTheme } from "@mui/material";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import {
  KlydoGreetOrderRange,
  useKlydoGreetOrderStats,
} from "../../../components/hooks/useKlydoGreetOrderStats";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSBox from "views/system-design/DSBox";
import DSStack from "views/system-design/DSStack";
import DSTypography from "views/system-design/DSTypography";
import DSTextField from "views/system-design/DSTextField";
import DSSelect from "views/system-design/DSSelect";
import DSMenuItem from "views/system-design/DSMenuItem";
import DSButton from "views/system-design/DSButton";
import DSCircularProgress from "views/system-design/DSCircularProgress";

const PRESET_DAYS = [7, 30, 90, 365] as const;

const todayStr = () => new Date().toISOString().slice(0, 10);

const isCustom = (
  range: KlydoGreetOrderRange,
): range is { from: string; to: string } => "from" in range;

const rangeLabel = (range: KlydoGreetOrderRange) =>
  isCustom(range) ? `${range.from} → ${range.to}` : `Last ${range.days} days`;

type StatTileProps = {
  label: string;
  value: string;
};

function StatTile({ label, value }: StatTileProps) {
  return (
    <DSBox sx={{ flex: 1, minWidth: 0, textAlign: "center" }}>
      <DSTypography variant="h4" sx={{ m: 0 }}>
        {value}
      </DSTypography>
      <DSTypography variant="body2" sx={{ color: "text.secondary", m: 0 }}>
        {label}
      </DSTypography>
    </DSBox>
  );
}

type StatRowProps = {
  title: string;
  subtitle: string;
  tiles: StatTileProps[];
};

function StatRow({ title, subtitle, tiles }: StatRowProps) {
  const theme = useTheme();
  return (
    <DSBox sx={{ width: "100%" }}>
      <DSTypography variant="subtitle2" sx={{ m: 0 }}>
        {title}
      </DSTypography>
      <DSTypography
        variant="caption"
        sx={{ color: "text.secondary", display: "block", mb: 1 }}
      >
        {subtitle}
      </DSTypography>
      <DSStack
        direction="row"
        spacing={2}
        divider={
          <DSBox
            sx={{
              width: "1px",
              alignSelf: "stretch",
              backgroundColor: theme.palette.divider,
            }}
          />
        }
      >
        {tiles.map((tile) => (
          <StatTile key={tile.label} {...tile} />
        ))}
      </DSStack>
    </DSBox>
  );
}

function KlydoGreetOrdersWidget() {
  const theme = useTheme();
  // The applied range that actually drives the query.
  const [range, setRange] = useState<KlydoGreetOrderRange>({ days: 30 });
  // Whether the custom date-range editor is shown.
  const [customMode, setCustomMode] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const { data: stats, isLoading, isError } = useKlydoGreetOrderStats(range);

  const selectValue =
    customMode || isCustom(range) ? "custom" : String(range.days);

  const handlePresetChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string;
    if (value === "custom") {
      setCustomMode(true);
      return;
    }
    setCustomMode(false);
    setRange({ days: Number(value) });
  };

  const applyCustomRange = () => {
    if (from && to && from <= to) {
      setRange({ from, to });
    }
  };

  const canApply = !!from && !!to && from <= to;

  return (
    <DSCard sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          pt: 2,
          gap: 1,
        }}
      >
        <DSStack direction="row" alignItems="center" spacing={1.5}>
          <CardGiftcardOutlinedIcon
            sx={{ fontSize: "2rem", color: "#E91E63" }}
          />
          <DSTypography variant="h6" sx={{ m: 0 }}>
            KlydoGreet Orders
          </DSTypography>
        </DSStack>
        <DSStack
          direction="row"
          alignItems="center"
          spacing={1}
          flexWrap="wrap"
          justifyContent="flex-end"
        >
          <DSSelect
            size="small"
            value={selectValue}
            onChange={handlePresetChange}
            sx={{ minWidth: 130 }}
          >
            {PRESET_DAYS.map((d) => (
              <DSMenuItem key={d} value={String(d)}>
                Last {d} days
              </DSMenuItem>
            ))}
            <DSMenuItem value="custom">Custom range</DSMenuItem>
          </DSSelect>
          {selectValue === "custom" && (
            <>
              <DSTextField
                type="date"
                size="small"
                label="From"
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: to || todayStr() }}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                sx={{ width: 150 }}
              />
              <DSTextField
                type="date"
                size="small"
                label="To"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: from, max: todayStr() }}
                value={to}
                onChange={(e) => setTo(e.target.value)}
                sx={{ width: 150 }}
              />
              <DSButton
                size="small"
                variant="contained"
                disabled={!canApply}
                onClick={applyCustomRange}
              >
                Apply
              </DSButton>
            </>
          )}
        </DSStack>
      </DSBox>
      <DSCardContent sx={{ flex: 1, display: "flex", alignItems: "center" }}>
        {isLoading ? (
          <DSBox sx={{ textAlign: "center", width: "100%", p: "20px" }}>
            <DSCircularProgress />
          </DSBox>
        ) : isError || !stats ? (
          <DSBox
            sx={{
              textAlign: "center",
              width: "100%",
              p: "20px",
              color: "text.secondary",
            }}
          >
            Couldn't load KlydoGreet orders
          </DSBox>
        ) : (
          <DSStack spacing={2.5} sx={{ width: "100%" }}>
            <StatRow
              title="Added in cart"
              subtitle='Ticked "Is it a gift?" at checkout'
              tiles={[
                {
                  label: "Added KlydoGreet",
                  value: stats.klydoGreetOrders.toLocaleString(),
                },
                {
                  label: "Clock orders",
                  value: stats.clockOrders.toLocaleString(),
                },
                { label: "Attach rate", value: `${stats.attachRate}%` },
              ]}
            />
            <StatRow
              title="Greet written"
              subtitle="The customer actually wrote the greet after ordering"
              tiles={[
                {
                  label: "All orders with a greet",
                  value: stats.greetOrders.toLocaleString(),
                },
                {
                  label: "KlydoGreet orders with a greet",
                  value: stats.klydoGreetOrdersWithGreet.toLocaleString(),
                },
                {
                  label: "Completion rate",
                  value: `${stats.greetCompletionRate}%`,
                },
              ]}
            />
          </DSStack>
        )}
      </DSCardContent>
      <DSBox
        sx={{ borderTop: `1px solid ${theme.palette.divider}`, px: 2, py: 1.5 }}
      >
        <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
          {rangeLabel(range)} · by order date · excludes cancelled orders · any
          clock order can write a greet from the follow-up email, with or
          without KlydoGreet
        </DSTypography>
      </DSBox>
    </DSCard>
  );
}

export default KlydoGreetOrdersWidget;
